import EmailOutboundHeader, { OutboundEmailFields } from './EmailOutboundHeader';
import ReplyInput from '../Reply/ReplyInput/ReplyInput';
import { useCallback, useRef } from 'react';
import { useEmailContext } from '../../../../../contexts/EmailContext';
import { fetchContact } from '../../../../../utils/fetchContact';
import { endConversation } from '../../../../../utils/endConversation';
import { isOutboundFieldsValid } from '../../../../../utils/emailContactHelpers';
import { Box } from '@coolblue-development/freddy';

interface OutboundEmailComposerProps {
  onSendSuccess: () => void;
  onSendError: (message: string) => void;
}

const EMPTY_OUTBOUND_FIELDS: OutboundEmailFields = {
  to: [],
  otherRecipients: [],
  subject: ''
};

const EMPTY_EMAIL_DRAFTS = {} as Record<string, { outboundFields?: OutboundEmailFields }>;

const OutboundEmailComposer = ({ onSendSuccess, onSendError }: OutboundEmailComposerProps): JSX.Element | null => {
  const {
    selectedEmail,
    outboundContactId,
    setIsReplyOpen,
    setOutboundContactId,
    emailDrafts = EMPTY_EMAIL_DRAFTS,
    updateEmailDraft = () => {}
  } = useEmailContext();
  const hasSentRef = useRef(false);

  const handleFieldsChange = useCallback(
    (fields: OutboundEmailFields) => {
      if (!selectedEmail) {
        return;
      }

      updateEmailDraft(selectedEmail, { outboundFields: fields });
    },
    [selectedEmail, updateEmailDraft]
  );

  if (!selectedEmail) {
    return null;
  }

  const outboundFields = emailDrafts[selectedEmail]?.outboundFields ?? EMPTY_OUTBOUND_FIELDS;
  const isOutboundValid = isOutboundFieldsValid(outboundFields);

  const handleSuccess = (): void => {
    hasSentRef.current = true;
    setOutboundContactId(null);
    onSendSuccess();
  };

  // Called both after a successful send (cleanup) and when the agent explicitly discards.
  // Only end the conversation on an explicit discard — after sending, the contact stays
  // open in ACW so the agent can close it manually.
  const handleDiscard = (): void => {
    setIsReplyOpen(false);
    if (!hasSentRef.current && outboundContactId) {
      const contact = fetchContact(outboundContactId);
      if (contact) {
        endConversation(contact);
      }
    }
    hasSentRef.current = false;
  };

  return (
    <Box sx={{ margin: '16px' }}>
      <EmailOutboundHeader contactId={selectedEmail} fields={outboundFields} onChange={handleFieldsChange} />
      <ReplyInput
        onDiscard={handleDiscard}
        onSuccess={handleSuccess}
        onError={onSendError}
        isSendDisabled={!isOutboundValid}
        outboundFields={outboundFields}
      />
    </Box>
  );
};

export default OutboundEmailComposer;
